import React from "react";
import { CheckCircle2, ArrowDown, FileText, Wand2, Users, Mic, Footprints } from "lucide-react";
import { PHASE_GRADIENTS, phaseGradient } from "./LeftCards";

/**
 * "Where am I?" progress phases shown at the top of the classic dashboard.
 *
 * Visual style mirrors the reference image: round colored circles per phase,
 * a small badge in the top-right (number → green check when complete),
 * dashed-line connectors between circles, and an animated glow ring around
 * the CURRENT in-progress phase.
 *
 * Phase signals (mirror LeftCards):
 *   P1 = hasResume        (mustard)
 *   P2 = makeover exists  (burnt orange)
 *   P3 = network active   (teal)
 *   P4 = playbook built   (blue)
 *   P5 = stepping stone   (green)
 */
const PHASES = [
  { id: 1, key: "resume",    label: "Resume",    Icon: FileText,   anchor: '[data-testid="resume-card"]' },
  { id: 2, key: "tailor",    label: "Tailor",    Icon: Wand2,      anchor: '[data-testid="tailor-card"]' },
  { id: 3, key: "network",   label: "Network",   Icon: Users,      anchor: '[data-testid="network-card"]' },
  { id: 4, key: "interview", label: "Interview", Icon: Mic,        anchor: '[data-testid="interview-card"]' },
  { id: 5, key: "stones",    label: "Stones",    Icon: Footprints, anchor: '[data-testid="stones-card"]' },
];

function scrollToSelector(sel) {
  if (typeof document === "undefined") return;
  const el = document.querySelector(sel);
  if (el && el.scrollIntoView) {
    el.scrollIntoView({ behavior: "smooth", block: "center" });
  }
}

/* ── Inline keyframes — scoped so we don't touch global CSS. ──────────── */
const GLOW_STYLE_ID = "phase-progress-glow-anim";
function ensureGlowKeyframes() {
  if (typeof document === "undefined") return;
  if (document.getElementById(GLOW_STYLE_ID)) return;
  const style = document.createElement("style");
  style.id = GLOW_STYLE_ID;
  style.textContent = `
    @keyframes resmo-phase-glow {
      0%, 100% { box-shadow: 0 0 0 0 rgba(255,106,0,0.55), 0 0 0 6px rgba(255,106,0,0.18); }
      50%      { box-shadow: 0 0 0 4px rgba(255,106,0,0.30), 0 0 0 12px rgba(255,106,0,0.08); }
    }
    @keyframes resmo-phase-pulse {
      0%, 100% { transform: scale(1); }
      50%      { transform: scale(1.04); }
    }
  `;
  document.head.appendChild(style);
}

export default function PhaseProgressBar({ doneFlags }) {
  React.useEffect(() => { ensureGlowKeyframes(); }, []);

  const completed = doneFlags.filter(Boolean).length;
  const total = PHASES.length;
  const pct = Math.round((completed / total) * 100);
  const nextIdx = doneFlags.findIndex((d) => !d);
  const allDone = completed === total;

  return (
    <div
      className="bg-white"
      style={{
        border: "1px solid #E2E8F0",
        borderRadius: 12,
        boxShadow: "0 1px 2px rgba(15,27,45,0.04), 0 1px 3px rgba(15,27,45,0.06)",
      }}
      data-testid="phase-progress-bar"
    >
      {/* Header */}
      <div className="px-5 pt-4 pb-2 flex items-center gap-3 flex-wrap">
        <div className="flex-1 min-w-[180px]">
          <div className="font-mono-rm uppercase text-[10px] tracking-widest"
               style={{ color: "#64748B", fontWeight: 600 }}>
            · Your progress
          </div>
          <div className="font-display text-base leading-tight"
               style={{ color: "#0F1B2D", letterSpacing: "-0.01em", fontWeight: 600 }}>
            {allDone
              ? `All ${total} phases complete — keep tailoring.`
              : `${completed} of ${total} phases complete · ${pct}%`}
          </div>
        </div>
        {!allDone && nextIdx >= 0 && (
          <button
            type="button"
            onClick={() => scrollToSelector(PHASES[nextIdx].anchor)}
            className="inline-flex items-center gap-2 px-3 py-1.5 text-[11px] font-semibold transition-transform hover:-translate-y-0.5"
            style={{
              background: phaseGradient(PHASES[nextIdx].id),
              color: "white",
              border: "none",
              borderRadius: 999,
              boxShadow: `0 4px 10px ${PHASE_GRADIENTS[PHASES[nextIdx].id].solid}40`,
            }}
            data-testid="phase-progress-jump-btn"
            title={`Jump to Phase ${PHASES[nextIdx].id} · ${PHASES[nextIdx].label}`}
          >
            <ArrowDown size={11} /> Next: Phase {PHASES[nextIdx].id} · {PHASES[nextIdx].label}
          </button>
        )}
      </div>

      {/* Phase circles row */}
      <div
        className="px-3 md:px-5 pt-2 pb-5 relative overflow-x-auto"
        data-testid="phase-progress-track"
      >
        <div className="flex items-start justify-between gap-1 sm:gap-2 md:gap-3 min-w-[480px]">
          {PHASES.map((p, i) => {
            const done = !!doneFlags[i];
            const isNext = !done && i === nextIdx;
            const palette = PHASE_GRADIENTS[p.id];
            const PhaseIcon = p.Icon;
            const showConnector = i < total - 1;
            const nextDone = !!doneFlags[i + 1];
            return (
              <React.Fragment key={p.key}>
                {/* Phase tile (circle + label) */}
                <div className="flex flex-col items-center flex-shrink-0"
                     style={{ width: 88, position: "relative" }}>
                  <button
                    type="button"
                    onClick={() => scrollToSelector(p.anchor)}
                    title={done ? "Done — jump here" : isNext ? "In progress — jump here" : "Jump here"}
                    aria-label={`Phase ${p.id} · ${p.label}${done ? " · complete" : isNext ? " · in progress" : ""}`}
                    className="relative flex items-center justify-center transition-transform hover:scale-105"
                    data-testid={`phase-progress-segment-${p.id}`}
                    style={{
                      width: 56, height: 56, borderRadius: "50%",
                      background: (done || isNext) ? phaseGradient(p.id) : "white",
                      color: (done || isNext) ? "white" : palette.solid,
                      border: (done || isNext) ? "none" : `2px solid #E2E8F0`,
                      cursor: "pointer",
                      boxShadow: (done || isNext)
                        ? `0 6px 14px ${palette.solid}45`
                        : "0 1px 2px rgba(15,27,45,0.05)",
                      animation: isNext ? "resmo-phase-glow 2s ease-in-out infinite, resmo-phase-pulse 2s ease-in-out infinite" : "none",
                    }}
                  >
                    <PhaseIcon size={22} strokeWidth={2.2} />

                    {/* Top-right badge: green check (done) OR phase number */}
                    <span
                      className="absolute inline-flex items-center justify-center"
                      style={{
                        top: -4, right: -4,
                        width: 20, height: 20, borderRadius: "50%",
                        background: done ? "#047857" : (isNext ? "white" : "white"),
                        color: done ? "white" : (isNext ? palette.solid : "#64748B"),
                        border: done ? "2px solid white" : `2px solid ${palette.solid}`,
                        boxShadow: "0 1px 2px rgba(15,27,45,0.18)",
                        fontSize: 11, fontWeight: 800, lineHeight: 1,
                      }}
                      data-testid={`phase-progress-badge-${p.id}`}
                    >
                      {done ? <CheckCircle2 size={12} strokeWidth={2.8} /> : p.id}
                    </span>
                  </button>

                  {/* Label */}
                  <div
                    className="mt-2 text-[11px] uppercase tracking-wide font-semibold text-center"
                    style={{
                      color: done ? palette.solid : (isNext ? "#0F1B2D" : "#64748B"),
                      letterSpacing: "0.04em",
                    }}
                  >
                    {p.label}
                  </div>
                </div>

                {/* Dashed connector between circles */}
                {showConnector && (
                  <div
                    className="flex-1 self-center -mt-3"
                    style={{ minWidth: 24 }}
                    aria-hidden
                    data-testid={`phase-progress-connector-${p.id}`}
                  >
                    <div
                      style={{
                        height: 0,
                        borderTop: done && nextDone
                          ? `2px solid ${palette.solid}`
                          : done
                            ? `2px dashed ${palette.solid}`
                            : "2px dashed #CBD5E1",
                        opacity: done && nextDone ? 1 : 0.65,
                      }}
                    />
                  </div>
                )}

                {/* Keep machine-readable fill marker for backwards-compat tests */}
                <div
                  data-testid={`phase-progress-fill-${p.id}`}
                  style={{ display: "none" }}
                  data-done={done ? "true" : "false"}
                />
              </React.Fragment>
            );
          })}
        </div>
      </div>
    </div>
  );
}
